/**
 * AdminPanel — Company-wide settings, user/branch config, cross-branch run history.
 * Accessible from the sidebar. Persists settings to backend (GUI-only for now).
 */

import React, { useState, useEffect } from 'react';
import * as api from '../services/routeOptimizerService';
import apiService from '../../../services/apiService';

const DEFAULT_SETTINGS = {
  change_lock_days: 90,
  default_shift_hours: 8,
  default_service_minutes: 20,
  default_budget_minutes: 14,
  max_technicians_per_branch: 50,
  require_approval_before_deploy: true,
  auto_build_matrix: true,
  geocoding_provider: 'nominatim',
  geocoding_api_key: '',
  geocoding_rate_limit: 1,
};

export default function AdminPanel() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [runHistory, setRunHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('settings');

  // Users tab state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingUser, setSavingUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'user', branch_id: '' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [impersonating, setImpersonating] = useState(null);

  // Run history filters
  const [historySearch, setHistorySearch] = useState('');
  const [historyBranch, setHistoryBranch] = useState('');
  const [historyStatus, setHistoryStatus] = useState('');

  useEffect(() => {
    api.getAdminSettings().then(data => {
      if (data && Object.keys(data).length > 0) {
        setSettings(prev => ({ ...prev, ...data }));
      }
    });
    api.getAdminRunHistory().then(data => {
      setRunHistory(data || []);
    });
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setMessage('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveAdminSettings(settings);
      setMessage('Settings saved.');
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Fetch users when Users tab is activated
  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const data = await apiService.get('/auth/admin/users');
      setUsers(Array.isArray(data) ? data : data.users || []);
    } catch (err) {
      setUsersError(err.response?.data?.message || err.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
  }, [activeTab]);

  const startEdit = (user) => {
    setEditingUserId(user.id);
    setEditForm({ role: user.role, mode: user.mode || 'write', branch_id: user.branch_id || '', is_active: user.is_active, name: user.name });
  };

  const cancelEdit = () => {
    setEditingUserId(null);
    setEditForm({});
  };

  const saveUser = async (uid) => {
    setSavingUser(true);
    try {
      await apiService.put(`/auth/admin/users/${uid}`, editForm);
      setEditingUserId(null);
      await fetchUsers();
    } catch (err) {
      setUsersError(err.response?.data?.message || err.message || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    setUsersError('');
    try {
      await apiService.post('/auth/admin/users', newUser);
      setNewUser({ email: '', password: '', name: '', role: 'user', branch_id: '' });
      await fetchUsers();
    } catch (err) {
      setUsersError(err.response?.data?.message || err.message || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const impersonateUser = async (user) => {
    if (!window.confirm(`Assume identity of ${user.name || user.email}? You will see the app as this user.`)) return;
    setImpersonating(user.id);
    setUsersError('');
    try {
      const data = await apiService.post(`/auth/admin/impersonate/${user.id}`);
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('impersonating', JSON.stringify({ id: user.id, name: user.name, email: user.email }));
        window.location.reload();
      }
    } catch (err) {
      setUsersError(err.response?.data?.message || err.message || 'Impersonation not available — backend endpoint not configured');
    } finally {
      setImpersonating(null);
    }
  };

  const tabs = [
    { key: 'settings', label: 'Global Settings' },
    { key: 'geocoding', label: 'Geocoding' },
    { key: 'history', label: 'Run History' },
    { key: 'users', label: 'Users' },
    { key: 'integrations', label: 'Integrations' },
  ];

  return (
    <div className="ro-dashboard">
      {/* Breadcrumb */}
      <div className="ro-breadcrumb">
        <a href="/routeoptimizer">Dashboard</a>
        <span className="ro-breadcrumb-sep">{'\u203A'}</span>
        <span className="ro-breadcrumb-current">Administration</span>
      </div>

      <div className="ro-header">
        <h1>Administration</h1>
      </div>

      {/* Tab bar */}
      <div className="ro-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`ro-tab ${activeTab === t.key ? 'ro-tab-active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'settings' && (
        <div className="ro-panel">
          <h3>Global Defaults</h3>
          <p className="ro-form-description">
            These defaults apply to all new weeks. Existing weeks keep their own settings.
          </p>

          <div className="ro-form-grid">
            {/* Change Lock */}
            <div className="ro-form-group">
              <label>Change Lock Period (days)</label>
              <input
                type="number" min="0" max="365"
                aria-label="Change Lock Period (days)"
                value={settings.change_lock_days}
                onChange={e => handleChange('change_lock_days', parseInt(e.target.value))}
                className="ro-input ro-input-narrow"
              />
              <span className="ro-form-hint">
                Addresses cannot be reassigned within this period after a change.
              </span>
            </div>

            <div className="ro-form-row">
              <div className="ro-form-group">
                <label>Default Shift Hours</label>
                <input
                  type="number" min="1" max="24" step="0.5"
                  aria-label="Default Shift Hours"
                  value={settings.default_shift_hours}
                  onChange={e => handleChange('default_shift_hours', parseFloat(e.target.value))}
                  className="ro-input"
                />
              </div>
              <div className="ro-form-group">
                <label>Default Service Time (min)</label>
                <input
                  type="number" min="1" max="120"
                  aria-label="Default Service Time (min)"
                  value={settings.default_service_minutes}
                  onChange={e => handleChange('default_service_minutes', parseInt(e.target.value))}
                  className="ro-input"
                />
              </div>
            </div>

            <div className="ro-form-row">
              <div className="ro-form-group">
                <label>Default Budget (min)</label>
                <input
                  type="number" min="1" max="120"
                  aria-label="Default Budget (min)"
                  value={settings.default_budget_minutes}
                  onChange={e => handleChange('default_budget_minutes', parseInt(e.target.value))}
                  className="ro-input"
                />
              </div>
              <div className="ro-form-group">
                <label>Max Employees per Branch</label>
                <input
                  type="number" min="1" max="200"
                  aria-label="Max Employees per Branch"
                  value={settings.max_technicians_per_branch}
                  onChange={e => handleChange('max_technicians_per_branch', parseInt(e.target.value))}
                  className="ro-input"
                />
              </div>
            </div>

            {/* Lunch Break Defaults */}
            <h4 className="ro-subsection-title">Lunch Break Defaults</h4>
            <div className="ro-form-row">
              <div className="ro-form-group">
                <label>Lunch Duration (min)</label>
                <input
                  type="number" min="0" max="120"
                  aria-label="Lunch Duration (min)"
                  value={settings.default_lunch_duration_minutes ?? 30}
                  onChange={e => handleChange('default_lunch_duration_minutes', parseInt(e.target.value))}
                  className="ro-input"
                />
              </div>
              <div className="ro-form-group">
                <label>Earliest Start</label>
                <input
                  type="time"
                  aria-label="Lunch Earliest Start"
                  value={settings.default_lunch_earliest ?? '11:00'}
                  onChange={e => handleChange('default_lunch_earliest', e.target.value)}
                  className="ro-input"
                />
              </div>
              <div className="ro-form-group">
                <label>Latest Start</label>
                <input
                  type="time"
                  aria-label="Lunch Latest Start"
                  value={settings.default_lunch_latest ?? '14:00'}
                  onChange={e => handleChange('default_lunch_latest', e.target.value)}
                  className="ro-input"
                />
              </div>
            </div>
            <span className="ro-form-hint">
              Default lunch break parameters for new branches. Set duration to 0 to disable.
            </span>

            {/* Distance Method */}
            <h4 className="ro-subsection-title">Distance Calculation</h4>
            <div className="ro-form-group">
              <label>Distance Method</label>
              <select
                value={settings.distance_method ?? 'road'}
                onChange={e => handleChange('distance_method', e.target.value)}
                className="ro-input"
              >
                <option value="road">Road distances (OSRM / routing engine)</option>
                <option value="haversine">Haversine (straight-line estimate)</option>
                <option value="google">Google Maps Drive Time</option>
              </select>
              <span className="ro-form-hint">
                Road distances use the routing engine for accurate drive times. Haversine is a rough straight-line estimate. Google Maps requires API credentials in the Geocoding tab.
              </span>
            </div>

            {/* Toggles */}
            <h4 className="ro-subsection-title">Automation</h4>
            <div className="ro-toggle-group">
              <label className={`ro-toggle-label ${settings.require_approval_before_deploy ? 'ro-toggle-label-active' : ''}`}>
                <input
                  type="checkbox"
                  checked={settings.require_approval_before_deploy}
                  onChange={e => handleChange('require_approval_before_deploy', e.target.checked)}
                  className="ro-toggle-checkbox"
                />
                <div>
                  <div className="ro-toggle-label-title">Require approval before deploying routes</div>
                  <div className="ro-toggle-label-desc">Routes must be reviewed and approved before being sent to technicians</div>
                </div>
              </label>
              <label className={`ro-toggle-label ${settings.auto_build_matrix ? 'ro-toggle-label-active' : ''}`}>
                <input
                  type="checkbox"
                  checked={settings.auto_build_matrix}
                  onChange={e => handleChange('auto_build_matrix', e.target.checked)}
                  className="ro-toggle-checkbox"
                />
                <div>
                  <div className="ro-toggle-label-title">Auto-build distance matrix</div>
                  <div className="ro-toggle-label-desc">Automatically compute road distances when addresses are uploaded</div>
                </div>
              </label>
            </div>
          </div>

          <button className="ro-btn ro-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          {message && (
            <div className={message.startsWith('Error') ? 'ro-message-error' : 'ro-message-success'}>
              {message.replace(/^Error:\s*/, '')}
            </div>
          )}
        </div>
      )}

      {activeTab === 'geocoding' && (
        <div className="ro-panel">
          <h3>Geocoding Configuration</h3>
          <p className="ro-form-description">
            Primary geocoding uses local Nominatim (Docker, no rate limit). If an address is not found
            in OpenStreetMap, a commercial provider can be used as fallback. Set the API key below to enable.
          </p>

          <div className="ro-form-grid">
            <div className="ro-form-group">
              <label>Fallback Geocoding Provider</label>
              <select
                value={settings.geocoding_provider || 'nominatim'}
                onChange={e => handleChange('geocoding_provider', e.target.value)}
                className="ro-input"
                aria-label="Geocoding provider selection"
              >
                <option value="nominatim">None &mdash; Nominatim only (free)</option>
                <option value="google">Google Maps Geocoding API</option>
                <option value="mapbox">Mapbox Geocoding</option>
                <option value="here">HERE Geocoding</option>
              </select>
              <span className="ro-form-hint">
                {!settings.geocoding_provider || settings.geocoding_provider === 'nominatim'
                  ? 'Local Nominatim handles ~85% of addresses. Unmapped streets (new subdivisions) will fail without a fallback.'
                  : 'Used only when Nominatim cannot find an address. Typically 10-20 calls per batch.'}
              </span>
            </div>

            {settings.geocoding_provider && settings.geocoding_provider !== 'nominatim' && (
              <div className="ro-form-group">
                <label>API Key</label>
                <input
                  type="password"
                  value={settings.geocoding_api_key || ''}
                  onChange={e => handleChange('geocoding_api_key', e.target.value)}
                  className="ro-input"
                  placeholder="Enter API key"
                  autoComplete="off"
                  aria-label="Geocoding API key"
                />
                <span className="ro-form-hint">
                  Required for {settings.geocoding_provider === 'google' ? 'Google Maps' : settings.geocoding_provider === 'mapbox' ? 'Mapbox' : 'HERE'} geocoding.
                  {settings.geocoding_provider === 'google' && ' First $200/month free (40,000 requests).'}
                </span>
              </div>
            )}

            <div className="ro-form-group">
              <label>Rate Limit (requests/second)</label>
              <input
                type="number" min="1" max="50"
                value={settings.geocoding_rate_limit ?? 1}
                onChange={e => handleChange('geocoding_rate_limit', parseInt(e.target.value))}
                className="ro-input ro-input-narrow"
              />
              <span className="ro-form-hint">
                Maximum geocoding requests per second. Nominatim enforces a strict 1 request/second policy.
              </span>
            </div>
          </div>

          <button className="ro-btn ro-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Geocoding Settings'}
          </button>
          {message && (
            <div className={message.startsWith('Error') ? 'ro-message-error' : 'ro-message-success'}>
              {message.replace(/^Error:\s*/, '')}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (() => {
        const branches = [...new Set(runHistory.map(r => r.branch_name).filter(Boolean))].sort();
        const statuses = [...new Set(runHistory.map(r => r.status).filter(Boolean))].sort();
        const filtered = runHistory.filter(run => {
          if (historyBranch && run.branch_name !== historyBranch) return false;
          if (historyStatus && run.status !== historyStatus) return false;
          if (historySearch) {
            const q = historySearch.toLowerCase();
            const hay = [run.branch_name, run.week_name, run.tier, run.comment].filter(Boolean).join(' ').toLowerCase();
            if (!hay.includes(q)) return false;
          }
          return true;
        });
        return (
        <div className="ro-panel">
          <h3>Cross-Branch Run History</h3>
          <p className="ro-form-description">
            All optimization runs across all branches.
          </p>

          <div className="ro-inline-row" style={{ gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <input
              type="text"
              className="ro-input"
              placeholder="Search runs..."
              aria-label="Search run history"
              value={historySearch}
              onChange={e => setHistorySearch(e.target.value)}
              style={{ maxWidth: '220px' }}
            />
            {branches.length > 1 && (
              <select
                className="ro-input"
                aria-label="Filter by branch"
                value={historyBranch}
                onChange={e => setHistoryBranch(e.target.value)}
                style={{ maxWidth: '180px' }}
              >
                <option value="">All branches</option>
                {branches.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
            {statuses.length > 1 && (
              <select
                className="ro-input"
                aria-label="Filter by status"
                value={historyStatus}
                onChange={e => setHistoryStatus(e.target.value)}
                style={{ maxWidth: '160px' }}
              >
                <option value="">All statuses</option>
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            {(historySearch || historyBranch || historyStatus) && (
              <button className="ro-btn" onClick={() => { setHistorySearch(''); setHistoryBranch(''); setHistoryStatus(''); }}>
                Clear
              </button>
            )}
            <span className="ro-form-hint" style={{ marginLeft: 'auto' }}>
              {filtered.length} of {runHistory.length} runs
            </span>
          </div>

          {filtered.length === 0 ? (
            <p className="ro-empty">{runHistory.length === 0 ? 'No run history available.' : 'No runs match the current filters.'}</p>
          ) : (
            <table className="ro-preview-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Branch</th>
                  <th>Week</th>
                  <th>Status</th>
                  <th>Tier</th>
                  <th className="ro-text-right">Miles</th>
                  <th>Comment</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((run, i) => (
                  <tr key={i}>
                    <td>{run.timestamp ? new Date(run.timestamp).toLocaleDateString() : '\u2014'}</td>
                    <td>{run.branch_name || '\u2014'}</td>
                    <td>{run.week_name || '\u2014'}</td>
                    <td>
                      <span className={run.status === 'approved' ? 'ro-status-text-approved' : run.status === 'rejected' ? 'ro-status-text-rejected' : 'ro-status-text-pending'}>
                        {run.status === 'approved' ? '\u2713' : run.status === 'rejected' ? '\u2717' : '\u25CB'} {run.status || 'completed'}
                      </span>
                    </td>
                    <td>{run.tier || '\u2014'}</td>
                    <td className="ro-text-tabular">{run.total_miles ? Number(run.total_miles).toFixed(1) : '\u2014'}</td>
                    <td className="ro-text-truncate" title={run.comment || ''}>
                      {run.comment || '\u2014'}
                    </td>
                    <td>
                      {run.week_id && run.status === 'approved' && (
                        <button
                          className="ro-btn ro-btn-sm"
                          onClick={async () => {
                            try {
                              const result = await api.exportWeek(run.week_id, { approval: run.timestamp });
                              if (result.url) window.open(result.url, '_blank');
                            } catch (err) {
                              console.error('Export failed:', err);
                            }
                          }}
                        >
                          Export
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        );
      })()}

      {activeTab === 'users' && (
        <div className="ro-panel">
          <h3>User Management</h3>
          <p className="ro-form-description">
            Manage user accounts, roles, and branch assignments.
          </p>

          {usersError && (
            <div className="ro-message-error">{usersError}</div>
          )}

          {usersLoading ? (
            <p className="ro-empty">Loading users...</p>
          ) : users.length === 0 ? (
            <p className="ro-empty">No users found.</p>
          ) : (
            <table className="ro-preview-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Mode</th>
                  <th>Scope</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    {editingUserId === user.id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            className="ro-input"
                            aria-label="Edit user name"
                            value={editForm.name}
                            onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                          />
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <select
                            className="ro-input"
                            aria-label="Edit user role"
                            value={editForm.role}
                            onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}
                          >
                            <option value="super_admin">super_admin</option>
                            <option value="workspace_owner">workspace_owner</option>
                            <option value="company_manager">company_manager</option>
                            <option value="branch_manager">branch_manager</option>
                          </select>
                        </td>
                        <td>
                          <select
                            className="ro-input"
                            aria-label="Edit user mode"
                            value={editForm.mode || 'write'}
                            onChange={e => setEditForm(f => ({ ...f, mode: e.target.value }))}
                          >
                            <option value="write">write</option>
                            <option value="read">read-only</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="ro-input"
                            aria-label="Edit branch ID"
                            value={editForm.branch_id}
                            onChange={e => setEditForm(f => ({ ...f, branch_id: e.target.value }))}
                            placeholder="branch_id"
                          />
                        </td>
                        <td>
                          <label className="ro-toggle-label" style={{ margin: 0, padding: '4px 0' }}>
                            <input
                              type="checkbox"
                              className="ro-toggle-checkbox"
                              checked={editForm.is_active}
                              onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))}
                            />
                            {editForm.is_active ? 'Yes' : 'No'}
                          </label>
                        </td>
                        <td>
                          <button className="ro-btn ro-btn-primary" onClick={() => saveUser(user.id)} disabled={savingUser}>
                            {savingUser ? 'Saving...' : 'Save'}
                          </button>{' '}
                          <button className="ro-btn" onClick={cancelEdit}>Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{user.name || '\u2014'}</td>
                        <td>{user.email}</td>
                        <td>{user.role}</td>
                        <td>{user.mode === 'read' ? 'read-only' : 'write'}</td>
                        <td>{user.branch_id || user.company_id || user.workspace_id || '\u2014'}</td>
                        <td>
                          <span className={user.is_active ? 'ro-status-text-approved' : 'ro-status-text-rejected'}>
                            {user.is_active ? '\u2713 Yes' : '\u2717 No'}
                          </span>
                        </td>
                        <td>
                          <button className="ro-btn" onClick={() => startEdit(user)}>Edit</button>
                          {' '}
                          <button
                            className="ro-btn ro-btn-sm"
                            onClick={() => impersonateUser(user)}
                            disabled={impersonating === user.id}
                            title={`Login as ${user.name || user.email}`}
                          >
                            {impersonating === user.id ? '...' : 'Assume'}
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Create User form */}
          <h4 className="ro-subsection-title" style={{ marginTop: '24px' }}>Create User</h4>
          <form onSubmit={createUser} className="ro-form-grid">
            <div className="ro-form-row">
              <div className="ro-form-group">
                <label>Email</label>
                <input
                  type="email" required
                  className="ro-input"
                  value={newUser.email}
                  onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))}
                  placeholder="user@example.com"
                />
              </div>
              <div className="ro-form-group">
                <label>Password</label>
                <input
                  type="password" required minLength={6}
                  className="ro-input"
                  value={newUser.password}
                  onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))}
                  placeholder="Min 6 characters"
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div className="ro-form-row">
              <div className="ro-form-group">
                <label>Name</label>
                <input
                  type="text" required
                  className="ro-input"
                  value={newUser.name}
                  onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))}
                  placeholder="Full name"
                />
              </div>
              <div className="ro-form-group">
                <label>Role</label>
                <select
                  className="ro-input"
                  value={newUser.role}
                  onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}
                >
                  <option value="branch_manager">branch_manager</option>
                  <option value="company_manager">company_manager</option>
                  <option value="workspace_owner">workspace_owner</option>
                  <option value="super_admin">super_admin</option>
                </select>
              </div>
              <div className="ro-form-group">
                <label>Mode</label>
                <select
                  className="ro-input"
                  value={newUser.mode || 'write'}
                  onChange={e => setNewUser(u => ({ ...u, mode: e.target.value }))}
                >
                  <option value="write">write</option>
                  <option value="read">read-only</option>
                </select>
              </div>
              <div className="ro-form-group">
                <label>Branch ID</label>
                <input
                  type="text"
                  className="ro-input"
                  value={newUser.branch_id}
                  onChange={e => setNewUser(u => ({ ...u, branch_id: e.target.value }))}
                  placeholder="For branch_manager"
                />
              </div>
            </div>
            <button type="submit" className="ro-btn ro-btn-primary" disabled={creatingUser}>
              {creatingUser ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'integrations' && (
        <div className="ro-panel">
          <h3>FSM / PoolBrain Integration</h3>
          <p className="ro-form-description">
            Export approved routes directly to your field service management system.
            This integration is coming soon.
          </p>

          <div className="ro-form-grid">
            <div className="ro-form-group">
              <label>FSM Provider</label>
              <select className="ro-input" disabled>
                <option>PoolBrain</option>
                <option>ServiceTitan</option>
                <option>Skimmer</option>
                <option>Custom API</option>
              </select>
              <span className="ro-form-hint">
                Integration with PoolBrain is planned for a future release.
              </span>
            </div>

            <div className="ro-form-group">
              <label>API Endpoint</label>
              <input
                type="text"
                className="ro-input"
                placeholder="https://api.poolbrain.com/v1/routes"
                disabled
              />
            </div>

            <div className="ro-form-group">
              <label>API Key</label>
              <input
                type="password"
                className="ro-input"
                placeholder="Enter API key when available"
                disabled
                autoComplete="off"
              />
            </div>

            <div className="ro-form-group">
              <label>Auto-Push on Approval</label>
              <label className="ro-toggle-label">
                <input type="checkbox" className="ro-toggle-checkbox" disabled />
                <div>
                  <div className="ro-toggle-label-title">Automatically push routes to FSM on approval</div>
                  <div className="ro-toggle-label-desc">When enabled, approved routes will be sent to the FSM system immediately</div>
                </div>
              </label>
            </div>
          </div>

          <div className="ro-confirm-dialog" style={{ marginTop: '1rem' }}>
            <strong>Coming Soon</strong> — FSM integration is under development. Contact support for early access or custom integration needs.
          </div>
        </div>
      )}
    </div>
  );
}
